import { FC, useMemo } from 'react';
import { Container, Stack, LoadingOverlay, Button, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Practitioner, Reference } from '@medplum/fhirtypes';
import { useResource } from '@medplum/react';
import { useEncounterFromParams } from '@hooks/useEncounterFromParams';
import { EncounterDetailsCard } from '@/components/EncounterDetailsCard';
import { HL7, ARKON } from '@/consts/systems.consts';

export const EncounterPage: FC = () => {
  const { t } = useTranslation('encounters');
  const navigate = useNavigate();
  const encounter = useEncounterFromParams();

  // Extract patient reference and ensure it's a Patient (not Group)
  const patientRef = encounter?.subject;
  const patientResource = useResource(patientRef || undefined);
  const patient =
    patientResource?.resourceType === 'Patient' ? patientResource : undefined;

  // Extract doctors from participants (memoized for performance)
  const { attendingParticipant, referringParticipant, consultantParticipants } =
    useMemo(() => {
      const attending = encounter?.participant?.find((p) =>
        p.type?.some((t) =>
          t.coding?.some(
            (c) => c.code === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.ATND
          )
        )
      );

      const referring = encounter?.participant?.find((p) =>
        p.type?.some((t) =>
          t.coding?.some(
            (c) =>
              c.code === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.REFERRER
          )
        )
      );

      const consultants =
        encounter?.participant?.filter((p) =>
          p.type?.some((t) =>
            t.coding?.some(
              (c) =>
                c.code === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.CON
            )
          )
        ) || [];

      return {
        attendingParticipant: attending,
        referringParticipant: referring,
        consultantParticipants: consultants,
      };
    }, [encounter?.participant]);

  // Fetch attending doctor
  const attendingDoctor = useResource<Practitioner>(
    attendingParticipant?.individual as Reference<Practitioner> | undefined
  );

  // Fetch referring doctor
  const referringDoctor = useResource<Practitioner>(
    referringParticipant?.individual as Reference<Practitioner> | undefined
  );

  // Fetch consultant 1
  const consultant1 = useResource<Practitioner>(
    consultantParticipants[0]?.individual as Reference<Practitioner> | undefined
  );

  // Fetch consultant 2
  const consultant2 = useResource<Practitioner>(
    consultantParticipants[1]?.individual as Reference<Practitioner> | undefined
  );

  // Fetch consultant 3
  const consultant3 = useResource<Practitioner>(
    consultantParticipants[2]?.individual as Reference<Practitioner> | undefined
  );

  // Combine all fetched consultants
  const consultants = [consultant1, consultant2, consultant3].filter(
    (c): c is Practitioner => c !== undefined
  );

  // Extract additional notes from extension
  const additionalNotes =
    encounter?.extension?.find(
      (ext) => ext.url === `${ARKON.BASE}/StructureDefinition/encounter-notes`
    )?.valueString || null;

  const patientId = patientRef?.reference?.split('/')[1];

  const handleViewPatient = () => {
    if (patientId) {
      navigate(`/Patient/${patientId}`);
    }
  };

  if (!encounter) {
    return (
      <Container size="lg" py="xl">
        <LoadingOverlay visible />
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Encounter Details Card */}
        <EncounterDetailsCard
          encounter={encounter}
          patient={patient}
          attendingDoctor={attendingDoctor}
          referringDoctor={referringDoctor}
          consultants={consultants}
          additionalNotes={additionalNotes}
          encounterStatus={encounter.status}
          loading={!patient}
        />

        {/* Actions */}
        <Group gap="md" justify="flex-end" maw={600} mx="auto">
          <Button onClick={handleViewPatient} disabled={!patientId}>
            {t('detailsPage.viewPatient')}
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};

export default EncounterPage;
